/**
 * Simple window manager for handling popup windows
 */
export class WindowManager {
  private window: Window | null = null;

  /**
   * Opens a new window with optional initial content
   * @param url - Initial URL to load (default: "about:blank")
   * @param target - Target name for the window (default: "_blank")
   * @param features - Window features string (e.g., "width=800,height=600")
   * @returns The window instance or null if blocked
   */
  open(url: string = "about:blank", target: string = "_blank", features?: string): Window | null {
    this.window = window.open(url, target, features);
    // The popup later navigates to another site (e.g. Moodle); without an opener that site cannot script or redirect
    // this app's window. Not "noopener" in features: window.open would then return null and we lose the handle.
    if (this.window) {
      try {
        this.window.opener = null;
      } catch (e) {}
    }
    return this.window;
  }

  /**
   * Sets HTML content for the window. The window is same-origin (about:blank), so `html` must be trusted markup:
   * escape every interpolated value with utils/escapeHtml.
   * @param html - HTML string to set as the window content
   */
  setContent(html: string): void {
    if (this.window && this.window.document) {
      this.window.document.documentElement.innerHTML = html;
    }
  }

  /**
   * Redirects the window to a new URL
   * @param url - URL to navigate to
   */
  redirect(url: string): void {
    if (this.window && !this.window.closed) {
      this.window.location.href = url;
    }
  }

  /**
   * Closes the window if it's open
   */
  close(): void {
    if (this.window && !this.window.closed) {
      this.window.close();
    }
  }

  /**
   * Checks if the window is currently open
   */
  isOpen(): boolean {
    return this.window !== null && !this.window.closed;
  }

  /**
   * Gets the window reference (can be null)
   */
  getWindow(): Window | null {
    return this.window;
  }

  /**
   * Focuses the window if it's open
   */
  focus(): void {
    if (this.window && !this.window.closed) {
      this.window.focus();
    }
  }
}

/**
 * Creates a new WindowManager instance
 */
export const createWindowManager = (): WindowManager => {
  return new WindowManager();
};
